import { Link, Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BrandLockup from "@/components/layout/BrandLockup";
import ThemeToggle from "@/components/shared/ThemeToggle";
import { useAuthStore, getLoginUrl } from "@/lib/auth";
import { CalendarRange, BellRing, Users } from "lucide-react";

/**
 * § App shell gateway for /app.
 *
 * /app used to be a bare <Navigate to="/dashboard">, and ProtectedRoute then
 * hard-redirected unauthenticated visitors straight off-origin to the shared
 * Wade SSO card — so a first-time visitor at tripmuscle.com/app saw somebody
 * else's sign-in form with no TripMuscle chrome at all (no logo, no nav, no
 * footer, no product content). The 2026-09-30 park walk judged that surface
 * "an empty shell / staging scaffold" (episode
 * tripmuscle:app-shell:all:judge-flag:empty-shell-with-no-content-areas).
 *
 * This page keeps the visitor on our origin: a real TripMuscle shell — brand
 * lockup, nav, sign-in entry point, a taste of what's inside, and a footer —
 * and only leaves for the SSO flow when the visitor chooses to. Signed-in
 * visitors still land straight on the dashboard.
 */
export default function SignInPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const location = useLocation();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const next = location.pathname + location.search;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header>
        <nav className="border-b border-border">
          <div className="container mx-auto px-4 max-w-5xl py-4 flex items-center justify-between">
            <Link to="/" aria-label="TripMuscle home">
              <BrandLockup tagline="Everyone's Fare Search Coach" />
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/pricing"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Pricing
              </Link>
              <ThemeToggle />
            </div>
          </div>
        </nav>
      </header>

      <main className="flex-1 container mx-auto px-4 max-w-5xl py-14">
        <div className="max-w-2xl mx-auto text-center space-y-4">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Your trips are waiting behind sign-in
          </h1>
          <p className="text-muted-foreground text-lg">
            TripMuscle plans the trip with everyone who's coming, watches the
            fare around the clock, and keeps one itinerary you can share.
          </p>
          <div className="pt-2">
            <Button size="lg" asChild>
              <a href={getLoginUrl(next)}>Sign in to TripMuscle</a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Sign-in runs on Wade.Technology single sign-on — you'll come
            straight back here.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 pt-14 max-w-4xl mx-auto text-left">
          <div className="rounded-lg border border-border p-5 space-y-2">
            <CalendarRange className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2 className="font-semibold">Plan it together</h2>
            <p className="text-sm text-muted-foreground">
              Describe the trip once. Everyone's airports, dates and constraints
              go into one plan.
            </p>
          </div>
          <div className="rounded-lg border border-border p-5 space-y-2">
            <BellRing className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2 className="font-semibold">Watch the fare</h2>
            <p className="text-sm text-muted-foreground">
              Your coach keeps watch around the clock and tells you when the
              price moves.
            </p>
          </div>
          <div className="rounded-lg border border-border p-5 space-y-2">
            <Users className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2 className="font-semibold">One itinerary</h2>
            <p className="text-sm text-muted-foreground">
              Every traveller sees the same live itinerary — no more pasted
              screenshots.
            </p>
          </div>
        </div>
      </main>

      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 max-w-5xl text-center space-y-3">
          <a
            href="https://wade.technology"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-baseline hover:opacity-80 transition-opacity"
          >
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1.15rem",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: "#0F172A",
              }}
            >
              Wade<span style={{ color: "#22C55E" }}>.</span>Technology
            </span>
          </a>
          <p className="text-xs text-muted-foreground/60">
            &copy; {new Date().getFullYear()} Wade.Technology. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
